import { useIsSbsMode } from '../useIsSbs';
import useConversationOps from '../useConversationOps';
import { useCallback, useMemo } from 'react';
import { useConversationReadonlyValue, useMakeConversationCache } from '../conversation';
import { useConvCanNotForward } from '../conversationsHooks/useConversationBatchForward';

/**
 * 判断聊天头部的功能是否启用
 */
export const useChatHeaderOpen = (conversation: Conversation.Conversation) => {
  const isSbsMode = useIsSbsMode();
  const readonly = useConversationReadonlyValue();
  const { channel, botMark, blacklistFlag, conversationId } = conversation;
  const convCanNotForward = useConvCanNotForward();

  const botMarkEnable = isSbsMode && botMark;
  const adjustSalesStatusEnable = !isSbsMode && channel !== 'MERCADO';
  const toolsBarEnable = !isSbsMode && !readonly;
  const pauseAutoReplyEnable = !isSbsMode && channel !== 'WA_BIZ';
  const starEnable = !isSbsMode && !blacklistFlag;
  const blacklistEnable = !isSbsMode && channel !== 'WA_BIZ';
  const forwardEnable = !isSbsMode && !convCanNotForward(conversationId);

  return {
    /**
     * 机器人标记
     * SBS模式下显示
     */
    botMarkEnable,
    /**
     * 调整销售状态
     */
    adjustSalesStatusEnable,
    /**
     * 工具栏是否启用
     */
    toolsBarEnable,
    /**
     * 暂停自动回复
     */
    pauseAutoReplyEnable,
    /**
     * 星标是否启用
     */
    starEnable,
    /**
     * 拉黑是否启用
     */
    blacklistEnable,
    /**
     * 转发是否启用
     */
    forwardEnable,
  };
};

/**
 * 调整销售状态
 */
const SALES_STATUS_LIST = ['BEFORE_SALES_STATUS', 'DURING_SALES_STATUS', 'AFTER_SALES_STATUS'];
export const useAdjustSalesStatus = (conversation: Conversation.Conversation) => {
  const { modifyConversationV2 } = useConversationOps();
  const setConversation = useMakeConversationCache('set');
  const { salesStatus: conversationSalesStatus } = conversation;

  const salesStatus = useMemo(() => {
    if (SALES_STATUS_LIST.includes(conversationSalesStatus)) {
      return conversationSalesStatus;
    } else {
      return undefined;
    }
  }, [conversationSalesStatus]);

  const changeSalesStatus = useCallback(
    (nextSalesStatus: Conversation.Conversation['salesStatus']) => {
      const { conversationId } = conversation;
      modifyConversationV2(
        {
          conversationId,
          salesStatus: nextSalesStatus,
        },
        () => {
          setConversation({
            conversationId,
            salesStatus: nextSalesStatus,
          } as Conversation.Conversation);
        },
      );
    },
    [modifyConversationV2, setConversation, conversation],
  );

  return {
    salesStatus,
    changeSalesStatus,
  };
};
